import type { AxiosResponse } from 'axios'
import axios from 'axios'
import { DateTime } from 'luxon'

import environment from '@/environment'
import type {
  AddResourceSetResourcesRequest,
  ApiClearQueueResponse,
  ApiCreateLocalPqResourceSetResponse,
  ApiLocalPqResourceSet,
  ApiLocalPqResourceSetSummary,
  ApiLocalPqRun,
  ApiRunResourceSetResponse,
  ApiStartLocalPqRunResponse,
  CreateLocalPqResourceSetRequest,
  LocalPqResourceSet,
  LocalPqRun,
  PqCsvDownload,
  PqMarketCatalogueEntry,
  PqTestName,
  RunResourceSetMarketRequest,
  RunResourceSetTestRequest,
  StartLocalPqRunRequest,
  UpdateLocalPqResourceSetRequest,
} from '@/features/prequalification/localControl/types'
import { resourceSetPhase } from '@/features/prequalification/localControl/utils/aggregation'
import { withDecodedErrorBody } from '@/features/prequalification/localControl/utils/csvErrors'
import type { PqPhase, PrequalificationResourceSet } from '@/features/prequalification/types'
import { isPqPhase } from '@/features/prequalification/types'

/**
 * Real local-control PQ endpoints, backed by `local-pq-api-v1.yaml`
 * (base `/api/v1/prequalification-manager/local`). These are reached through
 * `pqClient`, never imported directly by hooks, so the real/mock wiring lives
 * in one place. Several signatures here anticipate backend changes the redesign
 * requires (see the endpoint table in the design doc); they go live by flipping
 * `USE_LOCAL_PQ_MOCKS` once the service catches up.
 */
export const LOCAL_PQ_API_URL = `${environment.services.prequalificationManagerApiUrl}/local`

function toDateTime(value: string): DateTime
function toDateTime(value: string | null | undefined): DateTime | null
function toDateTime(value: string | null | undefined): DateTime | null {
  return value ? DateTime.fromISO(value) : null
}

/**
 * The backend's `phase` enum feeds exhaustive switches and palette lookups in the
 * UI (`getPhaseChipProps`), so an unrecognized value must fail loudly at the
 * boundary rather than slip through as a blank chip. The thrown error surfaces to
 * the user through the query's `useErrorHandler` instead of corrupting the render.
 * Markets are not checked here: the catalogue is the backend's, and the UI labels
 * a market it doesn't know by its id.
 */
function ensurePhase(value: string, context: string): PqPhase {
  if (!isPqPhase(value)) {
    throw new Error(`Local PQ: unrecognized run phase "${value}" (${context})`)
  }
  return value
}

function deserializeRun(run: ApiLocalPqRun): LocalPqRun {
  return {
    id: run.id,
    resourceSetId: run.resourceSetId ?? null,
    resourceId: run.resourceId,
    resourceName: null,
    testName: run.testName,
    requestedAt: toDateTime(run.requestedAt),
    startAt: toDateTime(run.startAt),
    phase: ensurePhase(run.phase, `run ${run.id}`),
    lastStatusAt: toDateTime(run.lastStatusAt),
    updatedAt: toDateTime(run.updatedAt),
  }
}

function deserializeResourceSet(resourceSet: ApiLocalPqResourceSet): LocalPqResourceSet {
  return {
    id: resourceSet.id,
    scope: 'local-control',
    name: resourceSet.name,
    resources: resourceSet.resources.map((resource) => ({ resourceId: resource.resourceId, name: resource.name })),
    requestedAt: toDateTime(resourceSet.requestedAt),
    startAt: toDateTime(resourceSet.startAt),
    updatedAt: toDateTime(resourceSet.updatedAt),
  }
}

function deserializeResourceSetSummary(summary: ApiLocalPqResourceSetSummary): PrequalificationResourceSet {
  const markets = summary.markets.map((market) => ({
    market: market.market,
    phase: ensurePhase(market.phase, `resource set ${summary.id}`),
  }))
  return {
    id: summary.id,
    scope: 'local-control',
    name: summary.name,
    resourceCount: summary.resourceCount,
    markets,
    // Derive the headline phase from the market rollups rather than trusting the
    // backend's `summary.phase`: a set with no runs has no markets, so it reads as
    // NOT_STARTED here exactly as the detail header computes it from runs.
    phase: resourceSetPhase(markets),
    requestedAt: toDateTime(summary.requestedAt),
    startAt: toDateTime(summary.startAt),
    lastStatusAt: toDateTime(summary.lastStatusAt),
  }
}

export async function listLocalPqMarkets(): Promise<PqMarketCatalogueEntry[]> {
  const response = await axios.get<PqMarketCatalogueEntry[]>(`${LOCAL_PQ_API_URL}/markets`)
  return response.data
}

export async function listLocalPqResourceSets(): Promise<PrequalificationResourceSet[]> {
  const response = await axios.get<ApiLocalPqResourceSetSummary[]>(`${LOCAL_PQ_API_URL}/resource-sets`)
  return response.data.map(deserializeResourceSetSummary)
}

export async function getLocalPqResourceSet(resourceSetId: string): Promise<LocalPqResourceSet> {
  const response = await axios.get<ApiLocalPqResourceSet>(`${LOCAL_PQ_API_URL}/resource-sets/${resourceSetId}`)
  return deserializeResourceSet(response.data)
}

export async function listLocalPqResourceSetRuns(resourceSetId: string): Promise<LocalPqRun[]> {
  const response = await axios.get<ApiLocalPqRun[]>(`${LOCAL_PQ_API_URL}/resource-sets/${resourceSetId}/runs`)
  return response.data.map(deserializeRun)
}

export async function createLocalPqResourceSet(
  request: CreateLocalPqResourceSetRequest,
): Promise<ApiCreateLocalPqResourceSetResponse> {
  const response = await axios.post<ApiCreateLocalPqResourceSetResponse>(`${LOCAL_PQ_API_URL}/resource-sets`, request)
  return response.data
}

export async function updateLocalPqResourceSet(
  resourceSetId: string,
  request: UpdateLocalPqResourceSetRequest,
): Promise<void> {
  await axios.patch(`${LOCAL_PQ_API_URL}/resource-sets/${resourceSetId}`, request)
}

export async function addLocalPqResourceSetResources(
  resourceSetId: string,
  request: AddResourceSetResourcesRequest,
): Promise<void> {
  await axios.post(`${LOCAL_PQ_API_URL}/resource-sets/${resourceSetId}/resources`, request)
}

export async function removeLocalPqResourceSetResource(resourceSetId: string, resourceId: string): Promise<void> {
  await axios.delete(`${LOCAL_PQ_API_URL}/resource-sets/${resourceSetId}/resources/${resourceId}`)
}

export async function runLocalPqResourceSetTest(
  resourceSetId: string,
  request: RunResourceSetTestRequest,
): Promise<ApiRunResourceSetResponse> {
  const response = await axios.post<ApiRunResourceSetResponse>(
    `${LOCAL_PQ_API_URL}/resource-sets/${resourceSetId}/tests/${request.testName}/runs`,
    { startAt: request.startAt ?? null },
  )
  return response.data
}

export async function runLocalPqResourceSetMarket(
  resourceSetId: string,
  request: RunResourceSetMarketRequest,
): Promise<ApiRunResourceSetResponse> {
  const response = await axios.post<ApiRunResourceSetResponse>(
    `${LOCAL_PQ_API_URL}/resource-sets/${resourceSetId}/markets/${request.market}/runs`,
    { startAt: request.startAt ?? null },
  )
  return response.data
}

export async function startLocalPqRun(request: StartLocalPqRunRequest): Promise<ApiStartLocalPqRunResponse> {
  const response = await axios.post<ApiStartLocalPqRunResponse>(`${LOCAL_PQ_API_URL}/runs`, request)
  return response.data
}

export async function cancelLocalPqRun(runId: string): Promise<void> {
  await axios.post(`${LOCAL_PQ_API_URL}/runs/${runId}/cancel`)
}

export async function cancelLocalPqResourceSet(resourceSetId: string): Promise<void> {
  await axios.post(`${LOCAL_PQ_API_URL}/resource-sets/${resourceSetId}/cancel`)
}

export async function deleteLocalPqResourceSet(resourceSetId: string): Promise<void> {
  await axios.delete(`${LOCAL_PQ_API_URL}/resource-sets/${resourceSetId}`)
}

export async function clearLocalPqQueue(resourceId: string): Promise<ApiClearQueueResponse> {
  const response = await axios.post<ApiClearQueueResponse>(`${LOCAL_PQ_API_URL}/resources/${resourceId}/clear-queue`)
  return response.data
}

/** Per-resource result CSV. */
export async function downloadLocalPqRunCsv(runId: string): Promise<PqCsvDownload> {
  const response = await withDecodedErrorBody(() =>
    axios.get<Blob>(`${LOCAL_PQ_API_URL}/blob/${runId}/csv`, {
      responseType: 'blob',
      headers: { Accept: 'text/plain' },
    }),
  )

  return { blob: response.data, filename: parseContentDispositionFilename(response) }
}

/** Combined per-test CSV: the backend merges every resource set resource's run for this test into one file. */
export async function downloadLocalPqTestCsv(resourceSetId: string, testName: PqTestName): Promise<PqCsvDownload> {
  const response = await withDecodedErrorBody(() =>
    axios.get<Blob>(`${LOCAL_PQ_API_URL}/resource-sets/${resourceSetId}/tests/${testName}/csv`, {
      responseType: 'blob',
      headers: { Accept: 'text/plain' },
    }),
  )

  return { blob: response.data, filename: parseContentDispositionFilename(response) }
}

export function parseContentDispositionFilename(response: AxiosResponse): string | undefined {
  const contentDisposition = response.headers['content-disposition'] as string | undefined
  if (!contentDisposition) return undefined

  // RFC 6266 gives the extended form precedence, and a backend sending a non-ASCII
  // name may send *only* that form — reading the plain token alone would silently
  // fall back to the caller's generic filename.
  return extendedFilename(contentDisposition) ?? plainFilename(contentDisposition)
}

/** RFC 5987 `filename*=<charset>'<language>'<percent-encoded-name>`. */
function extendedFilename(contentDisposition: string): string | undefined {
  const match = /filename\*=[^']*'[^']*'([^;]+)/i.exec(contentDisposition)
  if (!match) return undefined

  try {
    return decodeURIComponent(match[1].trim())
  } catch {
    // A malformed percent sequence isn't worth failing a download over; fall
    // through to the plain token (or the caller's fallback name).
    return undefined
  }
}

/** The plain `filename=` token, optionally quoted and followed by further parameters. */
function plainFilename(contentDisposition: string): string | undefined {
  const parts = contentDisposition.split('filename=')
  if (parts.length <= 1) return undefined

  return parts[1]
    .split(';')[0]
    .trim()
    .replace(/^"(.*)"$/, '$1')
}
